import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useGoBack } from "@/common/hooks/useGoBack";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { FormTransformers } from "@/common/utils/formTransformers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import z from "zod";
import { useCreateMaintenanceForwarding } from "../hooks/useCreateMaintenanceForwarding";
import { useCreateForwardingsFormStore } from "../stores/createForwardingsFormStore";
import { MaintenanceForwardingsDirectiveFormProps } from "../types/maintenanceForwardingsShowDirectiveDetails.type";
import { buildDynamicSchema } from "../utils/buildDynamicSchema";
import { buildRequiredDirectiveSteps } from "../utils/buildRequiredDirectiveSteps";
import CreateForwardingDirectiveData from "./CreateForwardingDirectiveData";
import CreateForwardingDirectiveEntryFormSteps from "./CreateForwardingDirectiveEntryFormSteps";
import CreateMaintenanceForwardingHeader from "./CreateMaintenanceForwardingHeader";

const CreateForwardingDirectiveEntryForm = ({ directiveDetails }: MaintenanceForwardingsDirectiveFormProps) => {
  const { companyId, workflowId, directiveEntryType } = useParams();

  const navigate = useNavigate();

  const goBack = useGoBack();

  const queryClient = useQueryClient();

  const { hasUserPermission } = useUserPermissions();

  const {
    currentStep,
    updatedValueAt,
  } = useCreateForwardingsFormStore();

  const formElementRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const requiredDirectiveSteps = useMemo(() => {
    return buildRequiredDirectiveSteps(directiveEntryType, directiveDetails.required_directive_steps);
  }, []);

  const dynamicSchema = useMemo(() => {
    if (requiredDirectiveSteps) {
      return buildDynamicSchema(requiredDirectiveSteps);
    }

    return z.object({});
  }, []);

  type DynamicSchemaValidationSchema = z.infer<typeof dynamicSchema>;

  const formMethods = useForm<DynamicSchemaValidationSchema>({
    resolver: zodResolver(dynamicSchema),
  });

  const {
    handleSubmit,
    formState: { isValid }
  } = formMethods;

  const isFormValid = useMemo(() => {
    const isRequiredDirectiveStepsPresent = !!requiredDirectiveSteps;
    const totalSteps = requiredDirectiveSteps && Object.keys(requiredDirectiveSteps!).length;

    return isValid && (!isRequiredDirectiveStepsPresent || currentStep >= Number(totalSteps));
  }, [isValid, currentStep]);

  const {
    mutate: createMaintenanceForwarding,
    isPending: createMaintenanceForwardingIsPending,
  } = useCreateMaintenanceForwarding({
    workflowId,
    directiveEntryType,
    schema: dynamicSchema,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenanceWorkflow', workflowId] });
      queryClient.invalidateQueries({ queryKey: ['maintenanceWorkflowForwardingsDirectiveEntryKlasses', workflowId] });

      if (hasUserPermission('Admin::Maintenance::Workflows::FilterForwardingsDirectiveEntryKlasses')) {
        return navigate(`/companies/${companyId}/maintenance/workflows/${workflowId}/${directiveEntryType}`);
      }

      return goBack({
        fallback: `/companies/${companyId}/maintenance/workflows/${workflowId}`,
        searchLastRouteFromFallback: true,
      });
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setMessages })(error);
    },
  });

  const onSubmit: SubmitHandler<DynamicSchemaValidationSchema> = (data) => createMaintenanceForwarding(data);

  useEffect(() => {
    if (formElementRef.current) {
      const currentStepElement = formElementRef.current.querySelector(`[data-step="${currentStep}"]`);

      if (currentStepElement) {
        currentStepElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      } else {
        formElementRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }
  }, [currentStep, updatedValueAt]);

  return (
    <FormProvider {...formMethods}>
      <div className="inner-screen">
        <CreateMaintenanceForwardingHeader
          showCancelButton
          actions={(
            <Button
              disabled={!isFormValid}
              onClick={handleSubmit(onSubmit)}
              isLoading={createMaintenanceForwardingIsPending}
            >
              Confirmar
            </Button>
          )}
        />

        <Box ref={formElementRef} className="flex-1 overflow-auto p-4 flex flex-col gap-4">
          <CreateForwardingDirectiveData
            directiveDetails={directiveDetails}
          />

          {requiredDirectiveSteps && (
            <CreateForwardingDirectiveEntryFormSteps
              requiredDirectiveSteps={requiredDirectiveSteps}
            />
          )}
        </Box>

        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      </div>
    </FormProvider>
  );
}

export default CreateForwardingDirectiveEntryForm;
